import type { LoggedSet, PlanItem, SetKind } from "./api";
import { mround, type WarmupSet } from "./weights";

/**
 * Строки таблицы подходов на экране тренировки.
 *
 * Вынесено из экрана, чтобы проверять тестами: здесь решается, какой записанный
 * подход в какой строке. Ошибка тут глазом не видна, пока подход не окажется
 * под чужим номером — а тогда подбор веса на следующей тренировке уже испорчен.
 */

/** Строка таблицы: записанный подход либо запланированный пустой. */
export interface Row {
  index: number;
  setId: string | null;
  kind: SetKind;
  weight: number | null;
  reps: number | null;
  rpe: number | null;
  done: boolean;
  previous: { weight_kg: number | null; reps: number | null } | null;
}

/** Правка ещё не записанного подхода. В сеть не идёт: подхода пока нет. */
export type Draft = Partial<Pick<Row, "weight" | "reps">>;

/**
 * Разминочные подходы нумеруются отдельной полосой индексов.
 *
 * Иначе они конкурируют за order_index с рабочими, и добавленная разминка
 * переписала бы уже записанный подход.
 */
export const WARMUP_BAND = 900;

const isWarm = (index: number) => index >= WARMUP_BAND;

/** Ключ пункта плана. У добавленного на ходу нет строки программы — берём упражнение. */
export const keyOf = (item: PlanItem): string =>
  item.program_exercise_id === null ? `x${item.exercise.id}` : `p${item.program_exercise_id}`;

/** Подходы этого пункта: программные ищем по строке, добавленные — по упражнению. */
export const setsOf = (item: PlanItem, sets: LoggedSet[]): LoggedSet[] =>
  item.program_exercise_id === null
    ? sets.filter((s) => s.program_exercise_id === null && s.exercise_id === item.exercise.id)
    : sets.filter((s) => s.program_exercise_id === item.program_exercise_id);

function defaultReps(item: PlanItem, index: number): number | null {
  // У дропсета последовательность повторов задана жёстко — она и есть цель.
  if (item.rep_sequence?.length) return item.rep_sequence[index] ?? null;
  // §5: предзаполняем тем, что человек сделал в прошлый раз. Нижняя граница
  // диапазона — запасной вариант, когда истории ещё нет.
  return item.previous[index]?.reps ?? item.target_reps_min;
}

/** Сброс дропсета — не рабочий подход: в подбор веса он входить не должен. */
function defaultKind(item: PlanItem, index: number): SetKind {
  return item.rep_sequence?.length && index > 0 ? "drop" : "working";
}

/** Раскладка рабочих строк по программе — пока человек её не менял в зале. */
export function plannedKinds(item: PlanItem): SetKind[] {
  const planned = item.rep_sequence?.length ?? item.target_sets ?? 1;
  return Array.from({ length: planned }, (_, i) => defaultKind(item, i));
}

/**
 * Каждый сброс легче строки над ним.
 *
 * Движок отдаёт на дропсет вес верхнего подхода, и без этого все сбросы
 * предлагались тем же весом — то есть заведомо неверным, его правили руками
 * каждый раз. 20 % — обычный шаг сброса; точнее подскажет только сам человек.
 */
const DROP_FACTOR = 0.8;

function prefillWeight(item: PlanItem, kind: SetKind, above: Row | undefined): number | null {
  if (kind === "drop" && above?.weight) return mround(above.weight * DROP_FACTOR);
  return item.recommendation.weight_kg;
}

/** Decimal с сервера может приехать строкой: «8.5» и 8.5 должны сравниваться одинаково. */
const toRpe = (value: number | string | null): number | null =>
  value === null ? null : Number(value);

/**
 * Строки упражнения: разминка, затем рабочие.
 *
 * `kinds` — раскладка рабочих строк: сколько их и какого типа. Записанный
 * подход строку не отдаёт, даже если раскладка короче: сделанное удаляется
 * только явным действием, но не тем, что строка исчезла из-под него.
 */
export function buildRows(
  item: PlanItem,
  sets: LoggedSet[],
  warmup: WarmupSet[],
  kinds: SetKind[] = plannedKinds(item),
): Row[] {
  const mine = setsOf(item, sets);
  const logged = mine.filter((s) => s.kind !== "warmup");
  const loggedWarm = mine.filter((s) => s.kind === "warmup");

  const warmRows: Row[] = warmup.map((w, i) => {
    const done = loggedWarm.find((s) => s.order_index === WARMUP_BAND + i);
    return {
      index: WARMUP_BAND + i,
      setId: done?.client_id ?? null,
      kind: "warmup",
      weight: done ? done.weight_kg : w.weight,
      reps: done ? done.reps : w.reps,
      rpe: done ? toRpe(done.rpe) : null,
      done: Boolean(done),
      previous: null,
    };
  });

  // Разминка, записанная в прошлый заход на экран, но выпавшая из плана.
  for (const s of loggedWarm) {
    if (warmRows.some((r) => r.index === s.order_index)) continue;
    warmRows.push({
      index: s.order_index,
      setId: s.client_id,
      kind: "warmup",
      weight: s.weight_kg,
      reps: s.reps,
      rpe: toRpe(s.rpe),
      done: true,
      previous: null,
    });
  }
  warmRows.sort((a, b) => a.index - b.index);

  const count = Math.max(kinds.length, ...logged.map((s) => s.order_index + 1));

  const workRows: Row[] = [];
  for (let i = 0; i < count; i++) {
    const done = logged.find((s) => s.order_index === i);
    const kind = done ? done.kind : kinds[i] ?? "working";
    workRows.push({
      index: i,
      setId: done?.client_id ?? null,
      kind,
      // Предзаполнение: рекомендация, у сброса — легче строки над ним.
      weight: done ? done.weight_kg : prefillWeight(item, kind, workRows[i - 1]),
      reps: done ? done.reps : defaultReps(item, i),
      rpe: done ? toRpe(done.rpe) : null,
      done: Boolean(done),
      previous: item.previous[i] ?? null,
    });
  }

  return [...warmRows, ...workRows];
}

export interface Removal {
  /** Записанный подход под удаляемой строкой. */
  deleteId: string | null;
  /** Записанные подходы ниже в той же полосе: каждый поднимается на строку. */
  moves: (Row & { setId: string })[];
  /** Раскладка рабочих строк после удаления; `null` — удалялась разминка. */
  kinds: SetKind[] | null;
  /** Какую ступень выбросить из плана разминки; `null` — план не трогаем. */
  warmupAt: number | null;
}

/**
 * Что сделать, чтобы строка исчезла, а не превратилась в дыру.
 *
 * Номера подходов — это order_index. Если просто стереть подход посреди
 * таблицы, под ним останется пустая строка с его номером, и «удалить» на деле
 * значило бы «очистить». Поэтому всё, что ниже, сдвигается на одну строку.
 * Сдвиг идёт только внутри своей полосы: удалённая разминка не трогает рабочие.
 */
export function planRemoval(rows: Row[], target: Row, warmupPlanned: number): Removal {
  const warm = isWarm(target.index);
  const band = rows.filter((r) => isWarm(r.index) === warm);
  const moves = band.filter(
    (r): r is Row & { setId: string } => r.done && r.setId !== null && r.index > target.index,
  );
  const position = target.index - WARMUP_BAND;
  return {
    deleteId: target.done ? target.setId : null,
    moves,
    kinds: warm ? null : band.filter((r) => r.index !== target.index).map((r) => r.kind),
    warmupAt: warm && position < warmupPlanned ? position : null,
  };
}

/** Черновики живут по ключу «пункт:строка» — после удаления они едут вслед за строками. */
export function shiftDrafts(
  drafts: Record<string, Draft>,
  key: string,
  removed: number,
): Record<string, Draft> {
  const out: Record<string, Draft> = {};
  for (const [k, draft] of Object.entries(drafts)) {
    const sep = k.lastIndexOf(":");
    const index = Number(k.slice(sep + 1));
    const affected =
      k.slice(0, sep) === key && isWarm(index) === isWarm(removed) && index >= removed;
    if (!affected) out[k] = draft;
    else if (index > removed) out[`${key}:${index - 1}`] = draft;
  }
  return out;
}

/** Что пересохранить, чтобы отменить удаление: сам подход и сдвинутые — на прежних местах. */
export const undoSaves = (target: Row, removal: Removal): (Row & { setId: string })[] => [
  ...(removal.deleteId ? [{ ...target, setId: removal.deleteId }] : []),
  ...removal.moves,
];

/**
 * Подходы, которые выглядят сделанными, но не засчитаны.
 *
 * Предзаполнение ставит в строку настоящие числа, и без галочки она легко
 * сходит за записанную — такой подход пропадал молча. Разминку не считаем:
 * пропустить её — обычное дело. Строку без повторов засчитывать нечем.
 */
export const pendingRows = (rows: Row[]): Row[] =>
  rows.filter((r) => !r.done && r.kind !== "warmup" && r.reps !== null);

/**
 * Текущий подход: следующая незасчитанная строка после последней засчитанной.
 *
 * Не просто первая незасчитанная: пропущенная разминка иначе навсегда держала
 * бы подсветку в начале списка. Если после последнего сделанного ничего не
 * осталось, возвращаемся к пропущенному.
 */
export function currentRow(lists: Row[][]): { list: number; index: number } | null {
  const flat = lists.flatMap((rows, list) => rows.map((row) => ({ list, row })));
  let last = -1;
  flat.forEach((x, i) => {
    if (x.row.done) last = i;
  });
  const next = flat.slice(last + 1).find((x) => !x.row.done) ?? flat.find((x) => !x.row.done);
  return next ? { list: next.list, index: next.row.index } : null;
}

const countsForRecord = (r: Row): r is Row & { weight: number } =>
  r.kind === "working" && Boolean(r.reps) && r.weight !== null;

/**
 * Строка, взявшая рекорд веса в этой тренировке.
 *
 * Только вес: оценку 1ПМ и объём считает сервер при завершении — формула Эпли
 * живёт в одном месте, в `app/domain.py`. Правила те же, что на сервере:
 * рабочий подход с повторами, строго тяжелее прежнего. Первая тренировка по
 * упражнению рекордом не отмечается — иначе значок горел бы на каждом движении.
 * Из равных по весу подходов рекорд у первого: второй его уже не бьёт.
 */
export function recordRow(rows: Row[], recordKg: number | null): number | null {
  if (recordKg === null) return null;
  let best: (Row & { weight: number }) | null = null;
  for (const r of rows) {
    if (!r.done || !countsForRecord(r) || r.weight <= recordKg) continue;
    if (best === null || r.weight > best.weight) best = r;
  }
  return best?.index ?? null;
}

/** Засчёт этой строки возьмёт рекорд: тяжелее и прежнего рекорда, и сделанного сегодня. */
export function beatsRecord(rows: Row[], row: Row, recordKg: number | null): boolean {
  if (recordKg === null || !countsForRecord(row)) return false;
  const today = rows.filter((r) => r.done && countsForRecord(r)).map((r) => r.weight as number);
  return row.weight > Math.max(recordKg, ...today);
}

/** Шкала с шагом в полбалла: ниже 6 RPE в зале не отличают от разминки. */
export const RPE_SCALE = [6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10] as const;

/** 8 — ещё можно, 9 и выше — у предела. Цвет чипа читается раньше цифры. */
export function rpeTone(rpe: number): "easy" | "hard" | "limit" {
  if (rpe >= 9) return "limit";
  if (rpe >= 8) return "hard";
  return "easy";
}

/** RPE понятнее через запас повторов: «8» говорит меньше, чем «2 в запасе». */
export function reserveLabel(rpe: number): string {
  const reserve = 10 - rpe;
  if (reserve <= 0) return "отказ";
  if (Number.isInteger(reserve)) return `${reserve} в запасе`;
  return `${Math.floor(reserve)}–${Math.ceil(reserve)} в запасе`;
}
